/**
 * After "sent": what Lever knows (read off RSC's stage changes — the tool writes nothing to Lever),
 * the reply once there is one, and the follow-up until then.
 *
 * The Lever block doubles as the recruiter's self-check: no opportunity 15 minutes after sending
 * means the profile was not exported from Recruiter; an opportunity with no "Reached Out" means
 * their Recruiter seat is probably not linked to Lever. Both are the recruiter's to fix, and both
 * were invisible before. A REPLY is not automatic (TA, Oct 13 2026): it shows here when the
 * recruiter exports the profile again in Recruiter, or clicks "They replied".
 */
import { useState, type ReactNode } from 'react';
import { Button } from '@finn-auto/design-system/atoms/button';
import type { OutreachCandidate } from '../../types/taOutreach';
import { CARD_FRAME, CARD_HEAD, CARD_TITLE } from './messageBox';

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'Europe/Berlin' });
const fmtTime = (iso: string) =>
  new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Berlin' });

/** The same card as the queue's InMail and profile check, for read-only content. */
function Panel({ title, hint, children }: { title: string; hint?: string; children: ReactNode }) {
  return (
    <section className={CARD_FRAME}>
      <div className={CARD_HEAD}>
        <h4 className={`m-0 ${CARD_TITLE}`}>{title}</h4>
        {hint && <span className="text-xs text-[#707070]">{hint}</span>}
      </div>
      <div className="px-4 py-3">{children}</div>
    </section>
  );
}

function LeverStatus({ candidate }: { candidate: OutreachCandidate }) {
  const { lever } = candidate;
  if (!lever) return <p className="text-[#707070]">Not checked yet.</p>;
  const checked = <span className="text-xs text-[#979797]"> · checked {fmtTime(lever.checkedAt)}</span>;
  if (lever.status === 'linked') {
    return (
      <div className="space-y-1">
        <p>
          <a href={lever.url || undefined} target="_blank" rel="noreferrer" className="text-[#0072EA] underline">
            Open in Lever ↗
          </a>
          {lever.matchedBy === 'name' && <span className="text-xs text-[#979797]"> · matched by name — check it is the right person</span>}
          {checked}
        </p>
        <ul className="m-0 list-none space-y-1 p-0 text-[13px]">
          <li>{lever.exportedAt ? `✓ Exported from Recruiter ${fmtTime(lever.exportedAt)}` : '— Export time unknown'}</li>
          <li>
            {lever.reachedOutAt
              ? `✓ InMail registered by Lever (“Reached Out”) ${fmtTime(lever.reachedOutAt)}`
              : lever.seat === 'no_signal'
                ? '✗ No “Reached Out” yet'
                : '… Waiting for Lever to register the InMail (usually within 15 minutes)'}
          </li>
          <li>
            {lever.respondedAt
              ? `✓ Reply registered (“Responded”) ${fmtTime(lever.respondedAt)}`
              : '— No reply registered. Lever does not notice a reply by itself: when it comes, export the profile again in Recruiter, or click “They replied” below.'}
          </li>
        </ul>
        {lever.seat === 'no_signal' && (
          <p className="rounded-md border border-solid border-[#F5C26B] bg-[#FFF8E6] px-3 py-2 text-[#7A4B00]">
            The profile is in Lever, but the InMail did not register. Your Recruiter seat is probably not linked to Lever — ask a Lever
            admin to check Settings → Integrations → LinkedIn. Until then, “They replied” below is how a reply gets recorded.
          </p>
        )}
      </div>
    );
  }
  if (lever.status === 'ambiguous') {
    return (
      <p className="rounded-md border border-solid border-[#F5C26B] bg-[#FFF8E6] px-3 py-2 text-[#7A4B00]">
        {lever.ambiguous} Lever profiles carry this name and none has the LinkedIn link. Add the link to the right one in Lever and the
        match is made on the next check.{checked}
      </p>
    );
  }
  return lever.seat === 'waiting' ? (
    <p className="text-[#707070]">Looking for the exported profile in Lever… (usually within 15 minutes of the export){checked}</p>
  ) : (
    <p className="rounded-md border border-solid border-[#F5C26B] bg-[#FFF8E6] px-3 py-2 text-[#7A4B00]">
      Not in Lever. The profile was not exported from Recruiter: open {candidate.name} in Recruiter and click “Export to Lever”. From then
      on Lever registers the InMail and the reply by itself.{checked}
    </p>
  );
}

/** What People Space did in Lever once they replied (proxy: lib/outreachLeverWrite.js). */
function LeverWriteStatus({ candidate }: { candidate: OutreachCandidate }) {
  const w = candidate.leverWrite;
  if (!w) return null;
  const text =
    w.state === 'test'
      ? w.action === 'would_move'
        ? 'Test mode: on the reply, the Lever opportunity would have been moved to “Responded”. Nothing was written.'
        : 'Test mode: on the reply, a Lever opportunity in “Responded” would have been created on this role. Nothing was written.'
      : w.state === 'done'
        ? w.action === 'created'
          ? `Added to Lever on the reply (“Responded”, ${fmtTime(w.at)}) — no export needed.`
          : w.action === 'moved'
            ? `Moved to “Responded” in Lever on the reply (${fmtTime(w.at)}).`
            : 'Lever already had the reply.'
        : w.state === 'needs_recruiter'
          ? `Not added to Lever: ${w.detail || 'several people match'}. Export the profile from Recruiter by hand.`
          : w.state === 'failed'
            ? `Lever could not be updated (${w.detail || 'error'}${w.attempts ? `, ${w.attempts} tries` : ''}). It is retried a few times; otherwise export by hand.`
            : 'Adding to Lever…';
  return <p className={`mt-2 ${w.state === 'failed' || w.state === 'needs_recruiter' ? 'text-[#B96716]' : 'text-[#707070]'}`}>{text}</p>;
}

export function OutreachSentPanel({
  candidate,
  busy,
  onCopyFollowUp,
  onCloseFollowUp,
  onFollowUpSent,
}: {
  candidate: OutreachCandidate;
  busy: boolean;
  onCopyFollowUp: () => void;
  onCloseFollowUp: (reason: 'replied' | 'no_reply') => void;
  /** "Follow-up sent": the recruiter sent it in LinkedIn themself. */
  onFollowUpSent: () => void;
}) {
  const { followUp, reply } = candidate;
  const [now] = useState(() => Date.now());
  const due = followUp && !followUp.closedAt ? Date.parse(followUp.dueAt) : null;
  const overdueDays = due && due < now ? Math.floor((now - due) / 86_400_000) : null;
  const sendFollowUp = followUp && !followUp.closedAt && !followUp.scheduledInLinkedIn && !reply;
  return (
    <div className="space-y-3 text-[13px]">
      <Panel title="Lever">
        <LeverStatus candidate={candidate} />
        <LeverWriteStatus candidate={candidate} />
      </Panel>

      {reply && (
        <Panel title="Reply" hint={`${fmtDate(reply.at)} · ${reply.source === 'lever' ? 'from Lever' : reply.source === 'gmail' ? 'LinkedIn notification in your Gmail' : 'recorded by you'}`}>
          {reply.preview ? <p className="text-[#1A1A1A]">“{reply.preview}”</p> : <p className="font-medium text-[#1B7F3B]">They replied.</p>}
          {reply.gmailThreadId && (
            <a href={`https://mail.google.com/mail/#all/${encodeURIComponent(reply.gmailThreadId)}`} target="_blank" rel="noreferrer" className="mt-1 inline-block text-[#0072EA] underline">
              Open the notification in Gmail ↗
            </a>
          )}
        </Panel>
      )}

      {followUp && !reply && (
        <div className="space-y-2">
          <div className={CARD_FRAME}>
            <div className={CARD_HEAD}>
              <span className={CARD_TITLE}>Follow-up</span>
              <span className="text-xs text-[#707070]">
                {followUp.closedAt
                  ? followUp.closeReason === 'sent'
                    ? `sent by you on ${fmtDate(followUp.sentAt || followUp.closedAt)}`
                    : `closed (${followUp.closeReason === 'no_reply' ? 'no answer' : 'by you'})`
                  : followUp.scheduledInLinkedIn
                    ? `scheduled in LinkedIn · goes out around ${fmtDate(followUp.dueAt)} if there is no reply`
                    : overdueDays !== null
                      ? `due ${overdueDays === 0 ? 'today' : `${overdueDays} day${overdueDays === 1 ? '' : 's'} ago`} · not scheduled in LinkedIn`
                      : `due ${fmtDate(followUp.dueAt)} if there is no answer · not scheduled in LinkedIn`}
              </span>
            </div>
            <p className="m-0 whitespace-pre-wrap px-4 py-3 text-[14px] leading-[1.6] text-[#1A1A1A]">{followUp.text}</p>
            {sendFollowUp && (
              <p className="m-0 border-t border-solid border-x-0 border-b-0 border-[#E9EAEC] px-4 py-2 text-xs text-[#707070]">
                This InMail went out without LinkedIn’s follow-up. “Write follow-up in LinkedIn” types this text into their composer — you
                read it and press Send there. LinkedIn blocks a new message for 24 hours after the first.
              </p>
            )}
          </div>
          {!followUp.closedAt && (
            <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
              <span className="flex flex-wrap items-center gap-2">
                {sendFollowUp && (
                  <Button variant="primary" size="sm" className="rounded-md" onClick={onCopyFollowUp}>
                    Write follow-up in LinkedIn
                  </Button>
                )}
                {sendFollowUp && (
                  <Button variant="outline" size="sm" className="rounded-md" onClick={onFollowUpSent} disabled={busy}>
                    Follow-up sent
                  </Button>
                )}
                <Button variant="outline" size="sm" className="rounded-md" onClick={() => onCloseFollowUp('replied')} disabled={busy}>
                  They replied
                </Button>
              </span>
              {!followUp.scheduledInLinkedIn && (
                <Button variant="ghost" size="sm" className="rounded-md text-[#707070]" onClick={() => onCloseFollowUp('no_reply')} disabled={busy}>
                  Close (no answer)
                </Button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
